/**
 * Copyright (c) [2026] Jangouts
 *
 * This software may be modified and distributed under the terms
 * of the MIT license.  See the LICENSE.txt file for details.
 */
/* eslint-disable indent */
/* eslint-disable react/jsx-indent */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { getCallLogPage } from "../../api/CallLog";
import janusApi from "../../janus-api";
import StreamsService from "../../utils/streams-service";
import { Play, Pause, StopCircle, X } from "react-feather";
import "./index.css";

// 状态映射
const STATUS_MAP = {
  0: { text: "未接通", color: "bg-gray-500", textColor: "text-gray-800" },
  1: { text: "已接通", color: "bg-green-500", textColor: "text-white" },
  2: { text: "已拒绝", color: "bg-red-500", textColor: "text-white" },
  3: { text: "已取消", color: "bg-yellow-500", textColor: "text-gray-800" },
  4: { text: "已结束", color: "bg-blue-500", textColor: "text-white" }
};

// 格式化时间
const formatTime = (timestamp) => {
  if (!timestamp) return "--";
  const date = new Date(timestamp);
  return date.toLocaleString("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  });
};

// 计算通话时长
const formatDuration = (startTime, endTime) => {
  if (!startTime || !endTime) return "--";
  const durationMs = endTime - startTime;
  const seconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  
  if (hours > 0) {
    return `${hours}小时${minutes % 60}分钟`;
  } else if (minutes > 0) {
    return `${minutes}分钟${seconds % 60}秒`;
  } else {
    return `${seconds}秒`;
  }
};

// 生成头像颜色
const getAvatarColor = (name) => {
  const colors = [
    "bg-blue-400", "bg-green-400", "bg-purple-400", 
    "bg-yellow-400", "bg-pink-400", "bg-indigo-400"
  ];
  const index = name ? name.charCodeAt(0) % colors.length : 0;
  return colors[index];
};

function CallHistoryPage() {
  const location = useLocation();
  const [atoken, setAtoken] = useState(null);

  // 解析 URL 参数1
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    setAtoken(searchParams.get('atoken'));
  }, [location.search]);

  const [loading, setLoading] = useState(false);
  const [callLogs, setCallLogs] = useState([]);
  const [pagination, setPagination] = useState({
    pageNo: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0
  });
  
  // 播放相关状态
  const [showPlayer, setShowPlayer] = useState(false);
  const [currentVideoId, setCurrentVideoId] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioStream, setAudioStream] = useState(null);
  const audioRef = useRef(null);
  


  const fetchCallLogs = useCallback(async (pageNo = 1) => {
    try {
      // 确保atoken存在
      if (!atoken) {
        console.error('Cannot fetch call logs: atoken is missing');
        return;
      }
      
      setLoading(true);
      const params = {
        pageNo,
        pageSize: pagination.pageSize,
        atoken: atoken
      };
      
      console.log('Fetching call logs with params:', params);
      const response = await getCallLogPage(params);
      console.log("Call logs response:", response);
      
      if (response.code === 0 && response.data) {
        setCallLogs(response.data.list || []);
        setPagination(prev => ({
          ...prev,
          pageNo,
          total: response.data.total || 0,
          totalPages: Math.ceil((response.data.total || 0) / pagination.pageSize)
        }));
      }
    } catch (error) {
      console.error("Failed to fetch call logs:", error);
    } finally {
      setLoading(false);
    }
  }, [atoken, pagination.pageSize]);

  useEffect(() => {
    // 当atoken或fetchCallLogs变化时重新获取数据
    console.log('useEffect triggered, atoken:', atoken);
    fetchCallLogs(1); // 重置到第一页
  }, [fetchCallLogs]);

   // Subscribe to Janus room events for audio playback
  useEffect(() => {
    const subscription = janusApi.getRoomSubject().subscribe((event) => {
      console.log("Room event:", event);
      // 转换为字符串进行比较，因为 currentVideoId 是字符串，而 event.payload.videoId 可能是数字
      const eventVideoIdStr = String(event.payload.videoId);
      const currentVideoIdStr = String(currentVideoId);
      
      if (event.event === 'audioStreamReady' && eventVideoIdStr === currentVideoIdStr) {
        const stream = StreamsService.get(event.payload.streamId);
        if (stream) {
          setAudioStream(stream);
          if (audioRef.current) {
            audioRef.current.srcObject = stream;
          }
        }
      } else if (event.event === 'audioStreamCleaned' && eventVideoIdStr === currentVideoIdStr) {
        setAudioStream(null);
        setIsPlaying(false);
        if (audioRef.current) {
          audioRef.current.srcObject = null;
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [currentVideoId]);

  const handlePageChange = (pageNo) => {
    if (pageNo >= 1 && pageNo <= pagination.totalPages) {
      fetchCallLogs(pageNo);
    }
  };

  const renderStatusBadge = (status) => {
    const statusInfo = STATUS_MAP[status] || STATUS_MAP[0];
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${statusInfo.color} ${statusInfo.textColor}`}>
        {statusInfo.text}
      </span>
    );
  };

  const renderCallerInfo = (log) => {
    const callerName = log.callerName || "未知用户";
    return (
      <div className="flex items-center">
        <div className={`w-10 h-10 ${getAvatarColor(callerName)} rounded-full flex items-center justify-center text-white font-semibold uppercase mr-3`}>
          {callerName.charAt(0)}
        </div>
        <div>
          <div className="font-medium">{callerName}</div>
        </div>
      </div>
    );
  };

  const renderAcceptorInfo = (log) => {
    const acceptorName = log.acceptorName || "未知用户";
    return (
      <div className="flex items-center">
        <div className={`w-10 h-10 ${getAvatarColor(acceptorName)} rounded-full flex items-center justify-center text-white font-semibold uppercase mr-3`}>
          {acceptorName.charAt(0)}
        </div>
        <div>
          <div className="font-medium">{acceptorName}</div>
        </div>
      </div>
    );
  };

  // 播放按钮渲染
  const renderPlayButton = (log) => {
    if (!log.videoId) {
      return (
        <span className="text-gray-400 text-sm">无录音</span>
      );
    }
    
    return (
      <button
        onClick={() => handlePlayClick(log.videoId)}
        className="inline-flex items-center px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors"
        title="播放录音"
      >
        <Play size={14} className="mr-1" />
        播放
      </button>
    );
  };

  // 处理播放点击
  const handlePlayClick = async (videoId) => {
    // 验证并转换 videoId 为数字
    if (!videoId) {
      console.error("Video ID is empty");
      return;
    }
    
    const videoIdNum = parseInt(videoId, 10);
    if (isNaN(videoIdNum) || videoIdNum <= 0) {
      console.error("Invalid video ID:", videoId);
      return;
    }
    
    setCurrentVideoId(videoId);
    setShowPlayer(true);
    setIsPlaying(false);
    setAudioStream(null);
    
    // 初始化播放器
    try {
      // 调用 Janus API 播放录音，传递数字类型的 ID
      console.log("Playing111 recording with ID (converted to number):", videoIdNum);
      const result = await janusApi.play(videoIdNum);
      console.log("Play result:", result);
      
      // 注意：实际音频流是通过 onremotetrack 回调获取的
      // 我们需要监听流的变化，这里先不处理
    } catch (error) {
      console.error("Failed to start playback:", error);
    }
  };

  // 处理播放/暂停
  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch(e => console.error("Play failed:", e));
      }
      setIsPlaying(!isPlaying);
    }
  };

  // 处理停止
  const handleStop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  // 关闭播放器
  const handleClosePlayer = async () => {
    // 停止播放
    handleStop();
    
    // 停止 Janus 播放会话
    if (currentVideoId) {
      try {
        await janusApi.stopPlay();
      } catch (error) {
        console.error("Failed to stop playback:", error);
      }
    }
    
    // 清理音频流
    if (audioStream) {
      audioStream.getTracks().forEach(track => track.stop());
      setAudioStream(null);
    }
    
    setShowPlayer(false);
    setCurrentVideoId(null);
    setIsPlaying(false);
  };

  // 监听音频流变化（从 Janus 回调获取）
  // 注意：实际项目中需要从 Janus 的回调中获取音频流
  // 这里简化处理，假设通过其他方式获取

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* 标题区域 */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800">通话记录</h1>
          <p className="text-gray-600 mt-2">查看所有通话历史记录</p>
        </div>
        
        {/* 表格区域 */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {/* 表格头部 */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                 <tr className="bg-gray-50">
                   <th className="px-6 py-4 text-left text-lg font-semibold text-gray-700 uppercase tracking-wider">
                    呼叫方
                  </th>
                   <th className="px-6 py-4 text-left text-lg font-semibold text-gray-700 uppercase tracking-wider">
                    被叫方
                  </th>
                   <th className="px-6 py-4 text-left text-lg font-semibold text-gray-700 uppercase tracking-wider">
                    时间
                  </th>
                   <th className="px-6 py-4 text-left text-lg font-semibold text-gray-700 uppercase tracking-wider">
                    通话时长
                  </th>
                   <th className="px-6 py-4 text-left text-lg font-semibold text-gray-700 uppercase tracking-wider">
                    状态
                  </th>
                   <th className="px-6 py-4 text-left text-lg font-semibold text-gray-700 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      </div>
                    </td>
                  </tr>
                ) : callLogs.length === 0 ? (
                  <tr>
                     <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                      暂无通话记录
                    </td>
                  </tr>
                ) : (
                   callLogs.map((log) => (
                     <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                       <td className="px-6 py-4 whitespace-nowrap">
                         {renderCallerInfo(log)}
                       </td>
                       <td className="px-6 py-4 whitespace-nowrap">
                         {renderAcceptorInfo(log)}
                       </td>
                       <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                         {formatTime(log.startTime ?? log.createTime)}
                       </td>
                       <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                         {formatDuration(log.startTime, log.endTime)}
                       </td>
                       <td className="px-6 py-4 whitespace-nowrap">
                         {renderStatusBadge(log.callStatus)}
                       </td>
                       <td className="px-6 py-4 whitespace-nowrap">
                         {renderPlayButton(log)}
                       </td>
                 </tr>
                   ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* 分页控件 */}
          {pagination.totalPages > 1 && (
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  显示第 {(pagination.pageNo - 1) * pagination.pageSize + 1} 到{" "}
                  {Math.min(pagination.pageNo * pagination.pageSize, pagination.total)} 条记录，
                  共 {pagination.total} 条
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(pagination.pageNo - 1)}
                    disabled={pagination.pageNo === 1}
                    className={`px-3 py-1 rounded-md text-sm font-medium ${
                      pagination.pageNo === 1
                        ? "text-gray-400 cursor-not-allowed"
                        : "text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    上一页
                  </button>
                  
                  {[...Array(Math.min(5, pagination.totalPages))].map((_, idx) => {
                    let pageNum;
                    if (pagination.totalPages <= 5) {
                      pageNum = idx + 1;
                    } else if (pagination.pageNo <= 3) {
                      pageNum = idx + 1;
                    } else if (pagination.pageNo >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + idx;
                    } else {
                      pageNum = pagination.pageNo - 2 + idx;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-1 rounded-md text-sm font-medium ${
                          pagination.pageNo === pageNum
                            ? "bg-blue-600 text-white"
                            : "text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => handlePageChange(pagination.pageNo + 1)}
                    disabled={pagination.pageNo === pagination.totalPages}
                    className={`px-3 py-1 rounded-md text-sm font-medium ${
                      pagination.pageNo === pagination.totalPages
                        ? "text-gray-400 cursor-not-allowed"
                        : "text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    下一页
                  </button>
         </div>

        </div>
      </div>
          )}
        </div>
      </div>

      {/* Audio Player Modal - moved outside pagination conditional */}
      {showPlayer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-800">播放录音</h2>
              <button
                onClick={handleClosePlayer}
                className="text-gray-500 hover:text-gray-700"
                title="关闭"
              >
                <X size={20} />
              </button>
            </div>
            
            {/* Content */}
            <div className="px-6 py-4">
              <div className="mb-4">
                <p className="text-gray-600">
                  录音 ID: <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">{currentVideoId}</span>
                </p>
              </div>
              
              {/* Audio element */}
              <audio 
                ref={audioRef} 
                autoPlay={isPlaying}
                className="w-full"
                controls
              />
              
              {/* Custom Controls */}
              <div className="flex items-center justify-center space-x-4 mt-6">
                <button
                  onClick={handlePlayPause}
                  disabled={!audioStream}
                  className={`inline-flex items-center px-4 py-2 rounded-lg font-medium ${
                    audioStream 
                      ? (isPlaying ? 'bg-yellow-500 hover:bg-yellow-600 text-white' : 'bg-green-500 hover:bg-green-600 text-white')
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {isPlaying ? (
                    <>
                      <Pause size={18} className="mr-2" />
                      暂停
                    </>
                  ) : (
                    <>
                      <Play size={18} className="mr-2" />
                      播放
                    </>
                  )}
                </button>
                
                <button
                  onClick={handleStop}
                  disabled={!audioStream}
                  className={`inline-flex items-center px-4 py-2 rounded-lg font-medium ${
                    audioStream 
                      ? 'bg-red-500 hover:bg-red-600 text-white'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <StopCircle size={18} className="mr-2" />
                  停止
                </button>
                
                <button
                  onClick={handleClosePlayer}
                  className="inline-flex items-center px-4 py-2 rounded-lg font-medium bg-gray-200 hover:bg-gray-300 text-gray-800"
                >
                  <X size={18} className="mr-2" />
                  关闭
                </button>
              </div>
              
              {/* Status */}
              <div className="mt-4 text-center">
                {!audioStream ? (
                  <p className="text-gray-500">正在加载音频流...</p>
                ) : isPlaying ? (
                  <p className="text-green-600">播放中...</p>
                ) : (
                  <p className="text-gray-600">已暂停</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CallHistoryPage;